import type { Coordinate } from 'ol/coordinate';
import MultiLineString from 'ol/geom/MultiLineString';

const chainCache = new WeakMap<MultiLineString, { readonly revision: number; readonly chained: MultiLineString }>();

const sameCoordinate = (a: Coordinate | undefined, b: Coordinate | undefined): boolean => Boolean(a && b && a[0] === b[0] && a[1] === b[1]);
const key = (coordinate: Coordinate): string => `${coordinate[0]},${coordinate[1]}`;

/**
 * The same lines, joined end to start into continuous polylines. A logical boundary (presentationBoundary) is one
 * 2-vertex segment per edge, so a wide stroke with round caps paints two caps at every vertex; joined, it paints one
 * round join there instead: the same footprint, far fewer sub-paths to rasterize, and dashes that run continuously.
 * Only exact shared coordinates are joined, so no line is moved, added or removed. Memoized per geometry revision.
 */
export function chainedLines(geometry: MultiLineString): MultiLineString {
  const cached = chainCache.get(geometry);
  if (cached && cached.revision === geometry.getRevision()) return cached.chained;
  // 1. Consecutive segments of a ring arrive in order: append while the next one starts where the last one ends.
  const runs: Coordinate[][] = [];
  geometry.getCoordinates().forEach((line) => {
    if (line.length < 2) return;
    const last = runs.at(-1);
    if (last && sameCoordinate(last.at(-1), line[0])) last.push(...line.slice(1)); else runs.push([...line]);
  });
  // 2. Runs that continue one another (a ring whose kept edges wrap past its first vertex).
  const byStart = new Map<string, number[]>();
  runs.forEach((run, index) => { const start = key(run[0]!); byStart.set(start, [...(byStart.get(start) ?? []), index]); });
  const used = new Array<boolean>(runs.length).fill(false);
  const chains: Coordinate[][] = [];
  runs.forEach((run, index) => {
    if (used[index]) return;
    used[index] = true; const chain = [...run];
    for (;;) {
      const next = byStart.get(key(chain.at(-1)!))?.find((candidate) => !used[candidate]);
      if (next === undefined) break;
      used[next] = true; chain.push(...runs[next]!.slice(1));
    }
    chains.push(chain);
  });
  const chained = new MultiLineString(chains);
  chainCache.set(geometry, { revision: geometry.getRevision(), chained });
  return chained;
}
